/**
 * Browser Push Notifications using Notification API
 */
import { NotificationPreference } from './api'

export class PushNotificationManager {
  private permission: NotificationPermission = 'default'
  private preferences: NotificationPreference | null = null

  constructor() {
    // Safely detect support for the Notification API (some mobile browsers don't expose it)
    if (typeof window !== 'undefined' && 'Notification' in window) {
      this.permission = window.Notification.permission
    } else {
      // Treat unsupported environments as denied so we never touch Notification directly
      this.permission = 'denied'
    }
  }

  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications')
      return 'denied'
    }

    if (this.permission === 'default') {
      this.permission = await Notification.requestPermission()
    }

    return this.permission
  }

  async setPreferences(preferences: NotificationPreference): Promise<void> {
    this.preferences = preferences
  }

  canShowNotification(): boolean {
    if (!('Notification' in window)) {
      return false
    }

    if (this.permission !== 'granted') {
      return false
    }

    if (!this.preferences?.browser_notifications_enabled) {
      return false
    }

    // Check quiet hours
    if (this.preferences.quiet_hours_enabled && this.isQuietHours()) {
      return false
    }

    return true
  }

  private isQuietHours(): boolean {
    if (!this.preferences?.quiet_hours_start || !this.preferences?.quiet_hours_end) {
      return false
    }

    const now = new Date()
    const currentTime = now.getHours() * 60 + now.getMinutes()
    const start = this.preferences.quiet_hours_start.split(':').map(Number)
    const end = this.preferences.quiet_hours_end.split(':').map(Number)
    const startTime = start[0] * 60 + start[1]
    const endTime = end[0] * 60 + end[1]

    if (startTime <= endTime) {
      return currentTime >= startTime && currentTime <= endTime
    } else {
      // Overnight quiet hours
      return currentTime >= startTime || currentTime <= endTime
    }
  }

  showNotification(
    title: string,
    options: NotificationOptions = {}
  ): Notification | null {
    if (!this.canShowNotification()) {
      return null
    }

    // Check category preference
    if (options.tag && this.preferences) {
      const category = options.tag
      if (!this.preferences.category_preferences[category]?.push) {
        return null
      }
    }

    const defaultOptions: NotificationOptions = {
      icon: '/logo192.png',
      badge: '/logo192.png',
      requireInteraction: false,
      ...options,
    }

    try {
      const notification = new Notification(title, defaultOptions)
      
      // Auto-close after 5 seconds (unless requireInteraction is true)
      if (!defaultOptions.requireInteraction) {
        setTimeout(() => {
          notification.close()
        }, 5000)
      }

      return notification
    } catch (error) {
      console.error('Failed to show notification:', error)
      return null
    }
  }

  closeNotification(notification: Notification): void {
    notification.close()
  }
}

// Singleton instance
export const pushNotificationManager = new PushNotificationManager()

